import React from 'react';
import { WrappedCard } from '../WrappedCard';
import { wrappedCopy } from '../../../lib/wrapped/wrappedCopy';

interface SpecialUnlocksCardProps {
  hasOBWIPrize: boolean;
  hasYellowstoneCompletion: boolean;
  hasDancingDaisy: boolean;
  hasGateUnlock: boolean;
  avatarUrl?: string;
  username?: string;
}

export const SpecialUnlocksCard: React.FC<SpecialUnlocksCardProps> = ({
  hasOBWIPrize,
  hasYellowstoneCompletion,
  hasDancingDaisy,
  hasGateUnlock,
  avatarUrl,
  username,
}) => {
  // Combine yellowstone and dancing daisy into one unlock
  const hasYellowstoneAdventure = hasYellowstoneCompletion || hasDancingDaisy;
  
  const unlocks = [
    { name: wrappedCopy.cards.specialUnlocks.obwiPrize, unlocked: hasGateUnlock, icon: '🥇' },
    { name: wrappedCopy.cards.specialUnlocks.yellowstone, unlocked: hasYellowstoneAdventure, icon: '🐴' },
  ];

  const unlockedCount = unlocks.filter((u) => u.unlocked).length;

  return (
    <WrappedCard className="relative" username={username}>
      <div className="space-y-4 md:space-y-6 relative z-10">
        {/* User avatar */}
        {avatarUrl && (
          <div className="flex justify-center mb-4 md:mb-6">
            <img
              src={avatarUrl}
              alt="User avatar"
              className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-yellow-400 shadow-2xl"
              style={{ objectPosition: 'center top' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/avatars/default.png';
              }}
            />
          </div>
        )}
        
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-yellow-300 to-amber-200 bg-clip-text text-transparent mb-4 md:mb-6 drop-shadow-2xl px-2">
          Gate Unlocks
        </h2>
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {unlocks.map((unlock, idx) => (
            <div
              key={idx}
              className={`p-4 md:p-5 rounded-xl border-2 shadow-2xl transition-transform hover:scale-105 ${
                unlock.unlocked
                  ? 'border-yellow-400/70 bg-black/60 backdrop-blur-md'
                  : 'border-yellow-400/30 bg-black/40 backdrop-blur-sm opacity-60'
              }`}
            >
              <div className="text-4xl md:text-5xl mb-2 md:mb-3">{unlock.icon}</div>
              <div
                className={`text-xs md:text-sm font-bold break-words ${
                  unlock.unlocked ? 'text-yellow-200' : 'text-yellow-400'
                }`}
              >
                {unlock.name}
              </div>
              <div className={`text-xs mt-1 md:mt-2 font-bold ${
                unlock.unlocked ? 'text-yellow-300' : 'text-yellow-400/60'
              }`}>
                {unlock.unlocked ? '✓ Unlocked' : 'Locked'}
              </div>
            </div>
          ))}
        </div>
        <div className="bg-yellow-500/20 backdrop-blur-sm rounded-xl p-3 md:p-4 border-2 border-yellow-400/50">
          <p className="text-base md:text-lg text-yellow-200 font-bold text-center">
            {unlockedCount} of {unlocks.length} unlocked
          </p>
        </div>
      </div>
    </WrappedCard>
  );
};

